import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { Avatar } from '../components/Avatar';
import type { DashboardData } from '../types';

function formatIls(agorot: number): string {
  const shekels = agorot / 100;
  return shekels >= 1000
    ? `₪${(shekels / 1000).toFixed(1)}K`
    : `₪${shekels.toFixed(2)}`;
}

function formatShortDate(iso: string, lang: string): string {
  return new Date(iso).toLocaleDateString(lang, {
    day: '2-digit',
    month: 'short',
  });
}

function formatEventTime(iso: string, lang: string): string {
  const d = new Date(iso);
  const today = new Date();
  const sameDay =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  if (sameDay) {
    return d.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleString(lang, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function Dashboard({ firmName }: { firmName: string }) {
  const { t, i18n } = useTranslation(['dashboard', 'common']);
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiFetch<DashboardData>('/api/dashboard')
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading && !data) {
    return (
      <div>
        <div className="full-panel">
          <div className="panel-header">{t('dashboard:welcome', { firmName })}</div>
          <div className="empty">{t('dashboard:loading')}</div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div>
        <div className="full-panel">
          <div className="panel-header">{t('dashboard:welcome', { firmName })}</div>
          <div className="empty">{t('dashboard:error')}</div>
        </div>
      </div>
    );
  }

  const lang = i18n.language;

  return (
    <div>
      <div className="metrics" style={{ marginBottom: 18 }}>
        <div className="metric">
          <div className="metric-label">{t('dashboard:metrics.open_cases')}</div>
          <div className="metric-val">{data.cases.open}</div>
          <div className="metric-sub">
            {t('dashboard:metrics.open_cases_sub', { pending: data.cases.pending })}
          </div>
        </div>
        <div className="metric">
          <div className="metric-label">{t('dashboard:metrics.total_clients')}</div>
          <div className="metric-val">{data.clients.total}</div>
          <div className="metric-sub">{t('dashboard:metrics.total_clients_sub')}</div>
        </div>
        <div className="metric">
          <div className="metric-label">{t('dashboard:metrics.outstanding_invoices')}</div>
          <div className="metric-val">{formatIls(data.invoices.outstandingAgorot)}</div>
          <div className="metric-sub down">
            {t('dashboard:metrics.outstanding_invoices_sub', {
              count: data.invoices.outstandingCount,
            })}
          </div>
        </div>
        <div className="metric">
          <div className="metric-label">{t('dashboard:metrics.hours_this_month')}</div>
          <div className="metric-val">{data.hoursThisMonth}</div>
          <div className="metric-sub up">
            {t('dashboard:metrics.hours_this_month_sub', {
              paid: formatIls(data.invoices.paidThisMonthAgorot),
            })}
          </div>
        </div>
        <div className="metric">
          <div className="metric-label">{t('dashboard:metrics.open_tasks')}</div>
          <div className="metric-val">{data.tasks.open}</div>
          <div
            className={`metric-sub ${data.tasks.overdue > 0 ? 'down' : 'up'}`}
          >
            {data.tasks.overdue > 0
              ? t('dashboard:metrics.open_tasks_sub_overdue', {
                  overdue: data.tasks.overdue,
                })
              : t('dashboard:metrics.open_tasks_sub_none')}
          </div>
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="full-panel">
          <div className="panel-header">{t('dashboard:panels.recent_clients')}</div>
          {data.recentClients.length === 0 ? (
            <div className="empty">{t('dashboard:panels.empty_clients')}</div>
          ) : (
            <ul className="dashboard-list">
              {data.recentClients.map((c) => (
                <li key={c.id} onClick={() => navigate(`/clients/${c.id}`)}>
                  <Avatar name={c.name} />
                  <div className="dashboard-list-main">
                    <div className="dashboard-list-title">{c.name}</div>
                    <div className="dashboard-list-sub">
                      {t(`dashboard:client_type.${c.type}`)}
                    </div>
                  </div>
                  <div className="dashboard-list-meta">
                    {formatShortDate(c.createdAt, lang)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="full-panel">
          <div className="panel-header">{t('dashboard:panels.recent_cases')}</div>
          {data.recentCases.length === 0 ? (
            <div className="empty">{t('dashboard:panels.empty_cases')}</div>
          ) : (
            <ul className="dashboard-list">
              {data.recentCases.map((c) => (
                <li key={c.id} onClick={() => navigate('/cases')}>
                  <div className="dashboard-list-main">
                    <div className="dashboard-list-title">
                      {c.caseNumber}
                      {c.area ? ` · ${c.area}` : ''}
                    </div>
                    <div className="dashboard-list-sub">
                      {c.clientName ?? '—'} ·{' '}
                      {t(`dashboard:case_status.${c.status}`)}
                    </div>
                  </div>
                  <div className="dashboard-list-meta">
                    {formatShortDate(c.openedAt, lang)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="full-panel">
          <div className="panel-header">{t('dashboard:panels.upcoming_events')}</div>
          {data.upcomingEvents.length === 0 ? (
            <div className="empty">{t('dashboard:panels.empty_events')}</div>
          ) : (
            <ul className="dashboard-list">
              {data.upcomingEvents.map((e) => (
                <li key={e.id} onClick={() => navigate('/calendar')}>
                  <div className="dashboard-list-main">
                    <div className="dashboard-list-title">{e.title}</div>
                    <div className="dashboard-list-sub">
                      {formatEventTime(e.start, lang)}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
